"use client";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
  Avatar,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack,
  ThumbUp,
  Comment,
  Visibility,
  Send,
  Subscriptions,
  Notifications,
  NotificationsActive,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router";
import { useCallback, useEffect, useState } from "react";
import { apiRemote, IDiscussionInfo, IForumInfo, ITopicInfo } from "@/api";
import { marked } from "marked";
import moment from "moment";
import { useUser } from "@/contexts/UserContext";
import { useAlert } from "@/contexts/AlertContext";
import ReactMarkdown from "react-markdown";
// import { useRouter } from "next/navigation"

const mockTopic = {
  id: 1,
  title: "Best practices for teaching robotics to beginners",
  author: "Jane Doe",
  authorAvatar: "/placeholder.svg?height=40&width=40",
  date: "March 15, 2024",
  likes: 42,
  comments: 15,
  views: 230,
  tags: ["robotics", "teaching"],
  content: `
    Teaching robotics to beginners can be both challenging and rewarding. Here are some key practices to consider:

    1. Start with the basics
    - Introduce fundamental concepts gradually
    - Use visual demonstrations
    - Provide hands-on experience

    2. Make it interactive
    - Encourage group work
    - Create engaging projects
    - Allow for experimentation

    3. Focus on problem-solving
    - Present real-world challenges
    - Break down complex tasks
    - Celebrate small victories
  `,
  list_comment: [
    {
      id: 1,
      author: "John Smith",
      avatar: "/placeholder.svg?height=40&width=40",
      content: "Great article! Very helpful for new teachers.",
      date: "2 hours ago",
    },
    {
      id: 2,
      author: "Alice Johnson",
      avatar: "/placeholder.svg?height=40&width=40",
      content: "I've used these techniques in my classroom with great success.",
      date: "1 day ago",
    },
  ],
};

export default function DiscussionDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const alert = useAlert();
  const [discussionInfo, setDiscussionInfo] = useState<
    IDiscussionInfo["data"] | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [likes, setLikes] = useState(0);
  const [subcribes, setSubcribes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState<IDiscussionInfo["data"]["comments"]>(
    []
  );
  const { user } = useUser();

  const handleSubscribe = () => {
    setSubscribed((prev) => !prev);
    setSubcribes((prev) => (subscribed ? prev - 1 : prev + 1));

    apiRemote
      .updateDiscussionSubscribe({
        topic_id: id,
        username: user?.user_login,
      })
      .then((data) => {
        if (data.code !== 200) {
          alert.error(data.message);
          setSubscribed((prev) => !prev); // Hoàn tác trạng thái
          setSubcribes((prev) => (subscribed ? prev + 1 : prev - 1)); // Hoàn tác số subscribe
        }
      })
      .catch((err) => {
        console.error("Subscribe failed", err);
        setSubscribed((prev) => !prev); // Hoàn tác trạng thái
        setSubcribes((prev) => (subscribed ? prev + 1 : prev - 1)); // Hoàn tác số subscribe
      });
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    apiRemote
      .getDiscussionDetailById(id)
      .then((data) => {
        setDiscussionInfo(data["data"]);
        setLikes(data["data"].total_favourite || 0);
        setSubcribes(data["data"].total_subscribe || 0);
        const userLiked = data["data"].favourites.some(
          (like: any) => like.user_name === user?.user_login
        );

        const userSubscribed = data["data"].subscribers.some(
          (sub: any) => sub.user_name === user?.user_login
        );
        setComments(data["data"].comments);
        setLiked(userLiked);
        setSubscribed(userSubscribed);
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
      });
  }, [id]);

  const handleLike = () => {
    setLiked((prev) => !prev);
    setLikes((prev) => (liked ? prev - 1 : prev + 1));
    apiRemote
      .updateDiscussionLike({
        topic_id: id,
        username: user?.user_login,
      })
      .then((data) => {
        if (data.code == 200) {
        } else {
          alert.error(data.message);
          setLiked((prev) => !prev); // Hoàn tác trạng thái liked
          setLikes((prev) => (liked ? prev + 1 : prev - 1)); // Hoàn tác số like
        }
      })
      .catch((err) => {
        console.error("Like failed", err);
        setLiked((prev) => !prev); // Hoàn tác trạng thái liked
        setLikes((prev) => (liked ? prev + 1 : prev - 1)); // Hoàn tác số like
      });
  };

  const handlePostComment = () => {
    if (!commentText.trim()) return; // Không cho phép gửi comment rỗng
    const newComment = {
      id: Date.now().toString(), // Đổi sang string để đúng kiểu id
      author: {
        id: user?.ID || "anonymous", // ID người dùng
        name: user?.display_name || "Anonymous", // Tên hiển thị
        user_name: user?.user_login || "anonymous", // Username
        avatar: user?.avatar_url || "/placeholder.svg?height=40&width=40", // Avatar
      },
      date: new Date().toISOString(), // Ngày tạo
      content: commentText, // Nội dung bình luận
    };
    // Cập nhật UI ngay lập tức để có trải nghiệm mượt mà
    setComments((prev = []) => [newComment, ...prev]);
    setCommentText("");
    // Gọi API để lưu comment vào server
    apiRemote
      .updateDiscussionComment({
        topic_id: id,
        username: user?.user_login,
        content: commentText,
      })
      .then((response) => {
        console.log("Comment posted:", response);
      })
      .catch((err) => {
        console.error("Failed to post comment", err);
        setComments((prev: any) => prev.filter((c: any) => c.id !== newComment.id)); // Xóa comment nếu lỗi
      });
  };
  return (
    <Box sx={{ width: "100%", mx: "auto" }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
        variant="outlined"
      >
        Back
      </Button>

      {/* Main Content */}
      <Card sx={{ mb: 4 }}>
        {loading ? (
          <Box
            sx={{
              p: 3,
              bgcolor: "white",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <CardContent>
            <Typography variant="h4" component="h1" gutterBottom>
              {discussionInfo?.title}
            </Typography>

            {/* Author Info & Metrics */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Avatar
                src="/placeholder.svg?height=40&width=40"
                alt={discussionInfo?.author.name}
              />
              <Typography variant="subtitle1">
                {discussionInfo?.author.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {discussionInfo?.modified
                  ? new Date(discussionInfo.modified).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )
                  : "N/A"}
              </Typography>
            </Stack>

            {/* Tags */}
            <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
              {/* {topicInfo?.tags?.map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  sx={{ bgcolor: "action.selected" }}
                />
              ))} */}
            </Stack>

            {/* Engagement Metrics */}
            <Stack direction="row" spacing={3} sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  onClick={handleLike}
                  variant={liked ? "contained" : "outlined"}
                  size="small"
                >
                  <ThumbUp sx={{ color: liked ? "white" : "inherit" }} />
                  <Typography sx={{ ml: 1 }}>{likes}</Typography>
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Comment color="action" />
                <Typography>{discussionInfo?.comments?.length}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                {/* <Visibility color="action" /> */}
                {/* <Notifications color="action" />
                <Typography>{subcribes}</Typography> */}
                <Button
                  onClick={handleSubscribe}
                  variant={subscribed ? "contained" : "outlined"}
                  size="small"
                >
                  {subscribed ? (
                    <NotificationsActive sx={{ color: "white" }} />
                  ) : (
                    <Notifications sx={{ color: "inherit" }} />
                  )}
                  <Typography sx={{ ml: 1 }}>{subcribes}</Typography>
                </Button>
              </Stack>
            </Stack>

            {/* Content */}
            <div
              dangerouslySetInnerHTML={{
                __html: marked(discussionInfo?.content || ""),
              }}
            />

            {/* Comments Section */}
            <Divider sx={{ mb: 4 }} />
            <Typography variant="h6" gutterBottom>
              Comments
            </Typography>

            {/* Add Comment */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Avatar src="/placeholder.svg?height=40&width=40" />
              <TextField
                fullWidth
                placeholder="Add a comment..."
                variant="outlined"
                size="small"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
              />
              <Button
                variant="contained"
                endIcon={<Send />}
                onClick={handlePostComment}
                disabled={!commentText.trim()}
              >
                Post
              </Button>
            </Stack>

            {/* Comments List */}
            <List>
              {comments?.map((comment) => (
                <ListItem key={comment.id} alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar src="/placeholder.svg?height=40&width=40" />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2">
                          {comment.author.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {comment.date
                            ? moment(comment.date).fromNow()
                            : "N/A"}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <div>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: marked.parse(comment.content || "", { breaks: true }),
                          }}
                        />
                      </div>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        )}
      </Card>
    </Box>
  );
}
